// MeetTheTeam.js
import React from 'react';
import { Heart, Shield, Users, Award, Phone } from 'lucide-react';

const MeetTheTeam = () => {
  const values = [
    {
      icon: Heart,
      title: "Compassion First",
      description: "Every member of our team is driven by genuine care and empathy for the older people and families we serve."
    },
    {
      icon: Shield,
      title: "Trust & Safety",
      description: "All carers undergo thorough background checks, training, and certification to support the highest standards of care."
    },
    {
      icon: Users,
      title: "Family-Centred Care",
      description: "We work closely with families, treating each client as we would our own loved ones, with dignity and respect."
    },
    {
      icon: Award,
      title: "Excellence in Service",
      description: "Our commitment to ongoing training and professional development helps us deliver thoughtful care every day."
    }
  ];

  const teamHighlights = [
    {
      title: "Certified & Trained Professionals",
      description: "Our carers hold relevant certifications and receive continuous training in aged care, dementia support, mobility assistance, and emergency response."
    },
    {
      title: "Experienced Care Coordinators",
      description: "Our care coordination team brings extensive experience in health and aged care, helping to organise seamless service delivery and personalised care plans."
    },
    {
      title: "Compassionate Support Staff",
      description: "From your first call to ongoing care, our administrative team provides friendly, responsive support to address your questions and concerns."
    },
    {
      title: "Specialised Care Experts",
      description: "We have carers with specialised training in areas such as Alzheimer's care, spinal cord injury support, hospice care, and post-operative recovery."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-cream pt-16 pb-16 md:pt-24 md:pb-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
            Meet The Team
          </p>
          <h1 className="font-display font-light text-ink leading-[1.1] text-4xl md:text-5xl mb-5">
            Meet Our Caring Team
          </h1>
          <p className="text-muted text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            At Wei Care, our people are our greatest strength. We are a dedicated group of professionals united by one shared purpose: to provide warm, compassionate care that helps older Australians live with dignity, comfort, and independence in their own homes.
          </p>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
              What Guides Us
            </p>
            <h2 className="font-display font-light text-ink leading-[1.1] text-3xl md:text-4xl lg:text-5xl">
              Our Core Values
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-primary bg-accent-soft mb-5">
                    <Icon size={28} className="text-primary" />
                  </div>
                  <h3 className="font-display font-normal text-ink text-xl leading-snug mb-3">
                    {value.title}
                  </h3>
                  <p className="text-muted text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Highlights Section */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
              Our People
            </p>
            <h2 className="font-display font-light text-ink leading-[1.1] text-3xl md:text-4xl lg:text-5xl">
              Who We Are
            </h2>
          </div>
          <div className="space-y-6">
            {teamHighlights.map((highlight, index) => (
              <div key={index} className="bg-white border border-line p-8 rounded-2xl">
                <h3 className="font-display font-normal text-ink text-xl leading-snug mb-3">
                  {highlight.title}
                </h3>
                <p className="text-muted text-base leading-relaxed">
                  {highlight.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
            Our Promise
          </p>
          <h2 className="font-display font-light text-ink leading-[1.1] text-3xl md:text-4xl lg:text-5xl mb-8">
            Our Commitment to You
          </h2>
          <div className="space-y-4 text-muted text-base md:text-lg leading-relaxed">
            <p>
              Every member of the Wei Care team, from carers to coordinators, is carefully chosen for their skills, their experience, and above all their heart for helping others. We believe quality care reaches beyond clinical support: it is about building genuine relationships, offering emotional reassurance, and treating each person with the respect and dignity they deserve.
            </p>
            <p>
              When you choose Wei Care, you are not simply engaging a service. You are welcoming a team of dedicated professionals who truly care about your loved one's wellbeing and your family's peace of mind.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
            Let's Talk
          </p>
          <h2 className="font-display font-light text-ink leading-[1.1] text-3xl md:text-4xl lg:text-5xl mb-5">
            Ready to Meet Your Care Team?
          </h2>
          <p className="text-muted text-base md:text-lg leading-relaxed mb-10">
            Contact us today to organise your complimentary in-home assessment and meet the carers who will support your family.
          </p>
          <a
            href="tel:+61480804640"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-medium text-sm px-7 py-3.5 rounded-full transition-colors"
          >
            <Phone size={18} />
            Call: +61 420 162 243
          </a>
        </div>
      </section>
    </div>
  );
};

export default MeetTheTeam;
